import {
  Activity,
  ArrowRight,
  Clock3,
  Coins,
  Eye,
  Network,
  Play,
  Radar,
  Radio,
  Route,
  Share2,
  ShieldCheck,
  TriangleAlert,
  UsersRound,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCount, formatDuration, formatNumber, formatRelative } from "../api";
import { useAuth } from "../auth";
import {
  ActivityRow,
  BoundaryCallout,
  Button,
  CoverageBar,
  DiagnosticRow,
  EmptyState,
  ErrorState,
  EvidenceChip,
  LoadingState,
  LoopRow,
  MetricBlock,
  ProviderMark,
  RhythmChart,
  Status,
  type EvidenceStage,
} from "../components";
import type { WindowComparison } from "../../shared/monitoring";
import { describeCostChange, describeReliabilityChange, describeRunsDelta, type ComparableWindow } from "./pulseComparison";
import { groupFindings, type Finding } from "./pulseFindings";
import { ShareCardModal } from "./ShareCardModal";
import "../styles/cockpit.css";

type LoopSummary = {
  id: string;
  name: string;
  cadence: string;
  team: { id: string; name: string } | null;
  owner?: { id: string; name: string } | null;
  visibility: { mode: "native" | "shared"; originOrganizationName: string };
  discovery: { source: { name: string | null; provider: string | null }; confidence: string | null } | null;
  performance: {
    runs: number;
    successes: number;
    failures: number;
    resolvedRuns: number;
    successRate: number | null;
    outcomeCoverage: number | null;
    averageDurationMs: number | null;
    lastRunAt: string | null;
    lastRunStatus: string | null;
  };
};

type TrendDatum = { day: string; runs: number; successes: number; failures: number };
type RunningRun = { id: string; loop_name: string; agent_name: string | null; started_at: string; status: string };
type PresentedStatus = "succeeded" | "failed" | "running" | "cancelled" | "unknown";

function presentedStatus(value: string | null | undefined): PresentedStatus {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "succeeded" || normalized === "success" || normalized === "completed") return "succeeded";
  if (normalized === "failed" || normalized === "error") return "failed";
  if (normalized === "running" || normalized === "queued") return "running";
  if (normalized === "cancelled" || normalized === "canceled") return "cancelled";
  return "unknown";
}

type OverviewData = {
  overview: {
    window: { days: number; since: string; through: string };
    metrics: {
      runs: number;
      successes: number;
      failures: number;
      resolvedRuns: number;
      liveRuns: number;
      cancelledRuns: number;
      successRate: number | null;
      outcomeCoverage: number | null;
      averageDurationMs: number | null;
      durationReportedRuns: number;
      runtimeCoverage: number | null;
      costUsd: number;
      costReportedRuns: number;
      costCoverage: number | null;
      tokens: number;
      activeLoops: number;
      reportingLoops: number;
      unobservedLoops: number;
      reportingCoverage: number | null;
      catalogSources: number;
    };
    /** Days 8–14, the same shape as `metrics` where it matters for comparison. */
    prior?: ComparableWindow & { window: { days: number; since: string; through: string } };
    comparison?: WindowComparison;
    trend: TrendDatum[];
    running: RunningRun[];
    activity: Array<{ id: string; summary: string; createdAt: string; actorType: string; verb: string }>;
  };
  loops: LoopSummary[];
  diagnostics: { findings: Finding[]; snoozed?: number };
};

function DeltaLine({ children }: { children: string | null }) {
  return children ? <span className="cockpit-delta">{children}</span> : null;
}

function fillSevenDays(values: TrendDatum[]): TrendDatum[] {
  const byDay = new Map(values.map((value) => [value.day, value]));
  const today = new Date();
  today.setUTCHours(12, 0, 0, 0);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(today);
    day.setUTCDate(today.getUTCDate() - (6 - index));
    const key = day.toISOString().slice(0, 10);
    return byDay.get(key) ?? { day: key, runs: 0, successes: 0, failures: 0 };
  });
}

function loopEvidence(loop: LoopSummary): { label: string; stage: EvidenceStage; detail: string } {
  if (loop.performance.resolvedRuns >= 3) return { label: "Enough evidence", stage: "evaluable", detail: `${loop.performance.resolvedRuns} resolved` };
  if (loop.performance.runs > 0) return { label: "Reporting", stage: "reporting", detail: `${loop.performance.resolvedRuns} resolved · early` };
  if (loop.discovery) return { label: "Connected", stage: loop.discovery.confidence === "inferred" ? "inferred" : "connected", detail: loop.discovery.confidence || "confidence unrated" };
  return { label: "Cataloged", stage: "cataloged", detail: "No runs reported yet" };
}

function sourceLabel(loop: LoopSummary): string {
  if (loop.visibility.mode === "shared") return loop.owner?.name ? `Shared by ${loop.owner.name}` : `Shared from ${loop.visibility.originOrganizationName}`;
  return loop.discovery?.source.name || loop.discovery?.source.provider || loop.team?.name || "Declared manually";
}

export function OverviewPage() {
  const { activeOrganization } = useAuth();
  const organizationId = activeOrganization?.id;
  const [data, setData] = useState<OverviewData | null>(null);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [snoozing, setSnoozing] = useState<string | null>(null);
  const [snoozeError, setSnoozeError] = useState("");

  async function snooze(findingId: string) {
    if (!organizationId) return;
    setSnoozing(findingId);
    setSnoozeError("");
    try {
      await api(`/api/app/diagnostics/${encodeURIComponent(findingId)}/snooze`, { method: "POST", body: JSON.stringify({ hours: 24 }) }, organizationId);
      const diagnostics = await api<OverviewData["diagnostics"]>("/api/app/diagnostics", {}, organizationId);
      setData((current) => (current ? { ...current, diagnostics } : current));
    } catch (caught) {
      setSnoozeError(caught instanceof Error ? caught.message : "The finding could not be snoozed.");
    } finally {
      setSnoozing(null);
    }
  }
  const [sharingCard, setSharingCard] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    const controller = new AbortController();
    setData(null);
    setError("");
    void api<OverviewData>("/api/app/overview", { signal: controller.signal }, organizationId)
      .then(setData)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setError(caught instanceof Error ? caught.message : "The operating picture is unavailable.");
      });
    return () => controller.abort();
  }, [organizationId, refreshKey]);

  if (!data) {
    return (
      <div className="page cockpit-page cockpit-state-page">
        {error ? (
          <ErrorState action={<Button onClick={() => setRefreshKey((value) => value + 1)}>Try again</Button>}>{error}</ErrorState>
        ) : (
          <LoadingState>Reading the operating picture…</LoadingState>
        )}
      </div>
    );
  }

  const { metrics } = data.overview;
  const hasLoops = metrics.activeLoops > 0;
  const evaluable = metrics.resolvedRuns >= 3 && metrics.successRate != null;
  const isPersonal = activeOrganization?.kind === "personal";
  const rhythmPoints = fillSevenDays(data.overview.trend).map((day) => ({
    label: new Date(`${day.day}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "short" }),
    succeeded: day.successes,
    failed: day.failures,
    unresolved: Math.max(day.runs - day.successes - day.failures, 0),
  }));
  const rhythmSummary = `${metrics.runs} observed runs over seven days: ${metrics.successes} succeeded, ${metrics.failures} failed, and ${Math.max(metrics.runs - metrics.resolvedRuns, 0)} remain unresolved or cancelled.`;
  const presentedFindings = groupFindings(data.diagnostics.findings, { activationHref: "/app/onboarding#activation", loopsHref: "/app/loops" });
  const visibleActivity = showAllActivity ? data.overview.activity : data.overview.activity.slice(0, 8);
  const reliabilityEvidence = evaluable
    ? `${metrics.successes} succeeded · ${metrics.failures} failed · ${metrics.resolvedRuns} resolved`
    : metrics.resolvedRuns > 0
      ? `${metrics.resolvedRuns} of 3 resolved outcomes for the first read`
      : "Waiting for 3 resolved runs";
  // Delta lines appear only when both seven-day windows are comparable; otherwise nothing is claimed.
  const { prior, comparison } = data.overview;
  const runsDelta = describeRunsDelta(comparison);
  const reliabilityDelta = describeReliabilityChange(metrics, prior, comparison);
  const costDelta = describeCostChange(metrics, prior, comparison);
  const snoozedCount = data.diagnostics.snoozed ?? 0;

  return (
    <div className="page cockpit-page overview-page">
      <header className="page-header cockpit-page-header">
        <div>
          <p className="eyebrow"><Radio size={10} aria-hidden="true" /> {isPersonal ? "Private workspace" : "Group"} · trailing {data.overview.window.days} days</p>
          <h1>Pulse</h1>
          <p>{hasLoops ? `${formatCount(metrics.activeLoops, "loop")} in view. ${metrics.reportingLoops} ${metrics.reportingLoops === 1 ? "is" : "are"} reporting.` : "No loops are visible in this workspace yet."}</p>
        </div>
        <div>
          {hasLoops && <Button variant="secondary" onClick={() => setSharingCard(true)}><Share2 size={15} aria-hidden="true" /> Share a status card</Button>}
          {!hasLoops && !isPersonal ? null : <Link className="button button--primary" to="/app/onboarding"><Play size={15} aria-hidden="true" /> {hasLoops ? "Add or activate a source" : "Scan my agents"}</Link>}
        </div>
      </header>
      {hasLoops && <ShareCardModal open={sharingCard} onClose={() => setSharingCard(false)} kind="status" />}

      {!hasLoops && !isPersonal ? (
        <EmptyState eyebrow="Group · nothing shared yet" title={`No loops have been shared into ${activeOrganization?.name ?? "this group"} yet`} action={<Link className="button button--primary" to="/app/people"><UsersRound size={15} aria-hidden="true" /> Invite people</Link>}>
          Loops stay in each person's private workspace until they share one here. Switch to your private workspace and share a loop from its Sharing page, or invite the teammates whose loops belong in this group.
        </EmptyState>
      ) : !hasLoops ? (
        <EmptyState eyebrow="Scan only · private by default" title="Map the loops already around you" action={<Link className="button button--primary" to="/app/onboarding">Create a private scan prompt <ArrowRight size={15} aria-hidden="true" /></Link>}>
          Start with any agent you use: Grok, Muse, dots, Claude, Codex, or another. The first pass catalogs operational metadata only; ongoing observers and loop edits are separate choices.
        </EmptyState>
      ) : (
        <>
          <section className="cockpit-command" aria-labelledby="pulse-summary-title">
            <div className="cockpit-command__read">
              <p className="eyebrow">Evidence summary · trailing {data.overview.window.days} days</p>
              <h2 id="pulse-summary-title">{metrics.reportingLoops > 0 ? `${metrics.reportingLoops} of ${formatCount(metrics.activeLoops, "loop")} reporting` : "No loop has reported a run yet"}</h2>
              <MetricBlock label="Reliability · resolved outcomes only" value={`${Math.round((metrics.successRate ?? 0) * 100)}%`} state={evaluable ? "value" : "withheld"} evidence={<>{reliabilityEvidence}<DeltaLine>{reliabilityDelta}</DeltaLine></>} icon={<ShieldCheck size={17} aria-hidden="true" />} />
              <p className={`cockpit-command__truth${evaluable ? "" : " cockpit-command__truth--withheld"}`}>{evaluable ? "Reliability counts resolved outcomes only. Running, cancelled, unresolved, and unknown work is excluded." : "Reliability appears after 3 resolved outcomes. Catalog presence is not a run outcome."}</p>
            </div>

            <nav className="cockpit-command__queue" aria-label="Operating queue">
              <p className="eyebrow">Needs attention</p>
              <a href="#in-motion"><span><Radio size={15} aria-hidden="true" /> In motion</span><strong>{data.overview.running.length}</strong><small>{data.overview.running.length ? "live run signals" : "nothing running"}</small></a>
              <a href="#diagnostics"><span><Radar size={15} aria-hidden="true" /> Needs review</span><strong>{presentedFindings.length}</strong><small>{presentedFindings.length === 1 ? "finding" : "findings"}</small></a>
              <Link to="/app/onboarding#activation"><span><Eye size={15} aria-hidden="true" /> Silent loops</span><strong>{metrics.unobservedLoops}</strong><small>{metrics.unobservedLoops ? "turn on run reporting" : "no runs reported yet"}</small></Link>
            </nav>

            <BoundaryCallout title="Outcomes come from the loop" tone="info">
              Catalog presence alone never proves a run occurred. Performance appears only where an agent reported an outcome.
            </BoundaryCallout>
          </section>

          <section className="cockpit-metrics" aria-label="Coverage and supporting metrics">
            <article className="cockpit-coverage-card">
              <div className="cockpit-coverage-card__label"><span>Catalog coverage</span><Eye size={17} aria-hidden="true" /></div>
              <p className="cockpit-coverage-source">{metrics.catalogSources} catalog {metrics.catalogSources === 1 ? "source" : "sources"} visible</p>
              <CoverageBar label="Loops reporting" value={metrics.reportingCoverage == null ? null : Math.round(metrics.reportingCoverage * 100)} detail={`${metrics.reportingLoops} of ${metrics.activeLoops} visible loops have run history · not success`} />
              <CoverageBar label="Outcome coverage" value={metrics.outcomeCoverage == null ? null : Math.round(metrics.outcomeCoverage * 100)} detail={metrics.runs ? `${metrics.resolvedRuns} of ${metrics.runs} observed runs resolved` : "No observed runs in this window"} />
            </article>
            <MetricBlock label="Observed runs · 7D" value={formatNumber(metrics.runs, 0)} evidence={<>{`${metrics.resolvedRuns} resolved outcomes`}<DeltaLine>{runsDelta}</DeltaLine></>} icon={<Route size={17} aria-hidden="true" />} />
            <MetricBlock label="Average runtime" value={formatDuration(metrics.averageDurationMs)} state={metrics.averageDurationMs == null ? "unknown" : "value"} evidence={metrics.durationReportedRuns ? `${metrics.durationReportedRuns} runs supplied duration` : "No duration data supplied"} icon={<Clock3 size={17} aria-hidden="true" />} />
            <MetricBlock label="Reported cost" value={`$${metrics.costUsd.toFixed(metrics.costUsd < 100 ? 2 : 0)}`} state={metrics.costReportedRuns ? "value" : "unknown"} evidence={<>{metrics.costReportedRuns ? `${metrics.costReportedRuns} runs supplied cost · ${Math.round((metrics.costCoverage ?? 0) * 100)}% coverage` : "No cost data supplied"}<DeltaLine>{costDelta}</DeltaLine></>} icon={<Coins size={17} aria-hidden="true" />} />
          </section>

          <div className="cockpit-primary-grid">
            <section className="panel cockpit-panel cockpit-rhythm-panel">
              <div className="panel__head">
                <div><p className="eyebrow">Execution rhythm · 7 days</p><h2>Every day remains visible</h2></div>
                <div className="cockpit-legend" aria-label="Chart legend"><span><i className="is-success" />Succeeded</span><span><i className="is-failed" />Failed</span><span><i className="is-neutral" />Open / other</span></div>
              </div>
              <RhythmChart points={rhythmPoints} summary={rhythmSummary} />
              <p className="cockpit-panel__note">Zero-run days remain in view. This rhythm shows volume, not a trend claim.</p>
            </section>

            <section className="panel cockpit-panel" id="in-motion">
              <div className="panel__head"><div><p className="eyebrow">Right now</p><h2>In motion</h2></div><span className="cockpit-count">{data.overview.running.length} live</span></div>
              {data.overview.running.length > 0 ? (
                <div className="cockpit-ledger-list">
                  {data.overview.running.map((run) => <LoopRow key={run.id} icon={<Radio size={16} aria-hidden="true" />} title={run.loop_name} meta={`${run.agent_name || "Unnamed agent"} · started ${formatRelative(run.started_at)}`} trailing={<Status value={presentedStatus(run.status)} />} />)}
                </div>
              ) : (
                <div className="cockpit-quiet"><Network size={23} aria-hidden="true" /><p>No runs are currently in motion.</p><small>The workspace is between beats; this is not a failure.</small></div>
              )}
            </section>
          </div>

          <div className="cockpit-secondary-grid">
            <section className="panel cockpit-panel cockpit-registry-panel">
              <div className="panel__head"><div><p className="eyebrow">Loop registry</p><h2>Loops and their evidence</h2></div><Link className="text-link" to="/app/loops">Open registry <ArrowRight size={14} aria-hidden="true" /></Link></div>
              {data.loops.length > 0 ? (
                <div className="cockpit-ledger-list">
                  {data.loops.map((loop) => {
                    const evidence = loopEvidence(loop);
                    const showRate = loop.performance.resolvedRuns >= 3 && loop.performance.successRate != null;
                    return (
                      <Link className="cockpit-loop-link" to={`/app/loops/${loop.id}`} key={loop.id}>
                        <LoopRow mark={<ProviderMark provider={loop.discovery?.source.provider} size={28} />} title={loop.name} meta={`${sourceLabel(loop)} · ${loop.cadence} · ${loop.performance.lastRunAt ? `last signal ${formatRelative(loop.performance.lastRunAt)}` : "no runs reported yet"}`} trailing={<span className="cockpit-loop-trailing"><Status value={presentedStatus(loop.performance.lastRunStatus)} /><EvidenceChip stage={evidence.stage}>{evidence.label}</EvidenceChip><small>{showRate ? `${Math.round(loop.performance.successRate! * 100)}% · ${loop.performance.resolvedRuns} resolved` : evidence.detail}</small></span>} />
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="cockpit-quiet"><p>Loop rows are unavailable in this response.</p><small>Open the registry to inspect the full visible set.</small><Link className="text-link" to="/app/loops">Open registry <ArrowRight size={14} aria-hidden="true" /></Link></div>
              )}
            </section>

            <section className="panel cockpit-panel" id="diagnostics">
              <div className="panel__head"><div><p className="eyebrow">Diagnostics</p><h2>What deserves a look</h2></div><span className="cockpit-panel__aside">{snoozedCount > 0 && <small className="cockpit-snoozed">{snoozedCount} snoozed</small>}<Radar size={20} aria-hidden="true" /></span></div>
              {presentedFindings.length > 0 ? (
                <div className="cockpit-ledger-list">
                  {presentedFindings.slice(0, 6).map((finding) => (
                    <div className="cockpit-diagnostic" key={finding.id}>
                      <Link className="cockpit-diagnostic-link" to={finding.href}><DiagnosticRow icon={finding.severity === "high" ? <TriangleAlert size={16} aria-hidden="true" /> : finding.grouped ? <Eye size={16} aria-hidden="true" /> : <Radar size={16} aria-hidden="true" />} title={finding.title} meta={finding.grouped ? "low priority · cataloged, not reporting" : `${finding.severity} priority`} detail={finding.detail} trailing={<ArrowRight size={14} aria-hidden="true" />} /></Link>
                      {!finding.grouped && <Button variant="ghost" className="cockpit-snooze" busy={snoozing === finding.id} disabled={snoozing !== null} onClick={() => void snooze(finding.id)} aria-label={`Snooze “${finding.title}” for 24 hours`}>Snooze 24h</Button>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cockpit-quiet"><ShieldCheck size={22} aria-hidden="true" /><p>No evidence-backed findings.</p><small>{snoozedCount > 0 ? `${snoozedCount} snoozed · diagnostics stay quiet until the data supports one.` : "Diagnostics stay quiet until the data supports one."}</small></div>
              )}
              {snoozeError ? <p className="cockpit-panel__note cockpit-panel__note--failure" role="alert">{snoozeError}</p> : null}
            </section>
          </div>

          <section className="panel cockpit-panel cockpit-activity-panel">
            <div className="panel__head"><div><p className="eyebrow">Activity</p><h2>Latest human and agent activity</h2></div><Activity size={20} aria-hidden="true" /></div>
            {data.overview.activity.length > 0 ? (
              <>
                <div className="cockpit-activity-grid">
                  {visibleActivity.map((item) => <ActivityRow key={item.id} icon={<span aria-hidden="true">{item.actorType === "agent" ? "A" : item.actorType === "system" ? "S" : "P"}</span>} title={item.summary} meta={`${item.actorType} · ${item.verb} · ${formatRelative(item.createdAt)}`} />)}
                </div>
                {data.overview.activity.length > 8 && <div className="cockpit-panel__more"><Button variant="ghost" onClick={() => setShowAllActivity((value) => !value)}>{showAllActivity ? "Show fewer" : `Show all ${data.overview.activity.length}`}</Button></div>}
              </>
            ) : (
              <div className="cockpit-quiet"><p>The activity ledger is ready.</p><small>Human, agent, and system changes will appear here when reported.</small></div>
            )}
          </section>

          <footer className="cockpit-footnote"><Eye size={14} aria-hidden="true" /><p><strong>Evidence boundary:</strong> reliability uses succeeded ÷ resolved outcomes. Running, queued, cancelled, and unknown runs are excluded. Cost and duration are agent-reported and display their coverage.</p></footer>
        </>
      )}
    </div>
  );
}
